import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { WordEditor } from '../../../admin/pages/NewsEdit/WordEditor';
import { newsApi } from '../../../api/news';
import { api } from "../../../api";
import s from './NewsEdit.module.css';

interface NewsForm {
    title: string;
    displayDescription: string;
    status: 'published' | 'draft' | 'archived';
    content: string;
}

interface NewsContent {
    html?: string;
    blocks?: Array<{
        type: string;
        content?: string;
        key?: string;
        alt?: string;
        src?: string;
    }>;
}

interface NewsResponse {
    id?: number;
    title: string;
    displayDescription: string;
    status: 'published' | 'draft' | 'archived';
    content: NewsContent;
    sortOrder?: number;
    dateCreating?: string;
}

export const NewsEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const isNew = location.pathname.includes('/admin/news/new');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState<NewsForm>({
        title: '',
        displayDescription: '',
        status: 'draft',
        content: '<p></p>'
    });

    useEffect(() => {
        if (!isNew && id) {
            loadNews(Number(id));
        }
    }, [id, isNew]);

    const loadNews = async (newsId: number) => {
        try {
            setLoading(true);
            const data: NewsResponse = await newsApi.getByIdAdmin(newsId);

            let htmlContent = '<p></p>';

            if (data.content) {
                if ('html' in data.content && data.content.html) {
                    htmlContent = data.content.html;
                }
                else if ('blocks' in data.content && data.content.blocks) {
                    const S3_URL = 'https://s3.twcstorage.ru/method-images/';
                    htmlContent = data.content.blocks.map((block) => {
                        if (block.type === 'paragraph') {
                            return `<p>${block.content || ''}</p>`;
                        } else if (block.type === 'image' && block.key) {
                            return `<img src="${S3_URL}${block.key}" alt="${block.alt || ''}" data-key="${block.key}" />`;
                        }
                        return '';
                    }).join('');
                }
            }

            setForm({
                title: data.title || '',
                displayDescription: data.displayDescription || '',
                status: data.status || 'draft',
                content: htmlContent
            });
        } catch (error) {
            console.error('Ошибка загрузки новости:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleContentChange = (content: string) => {
        setForm(prev => ({ ...prev, content }));
    };

    const extractImagesFromHTML = (html: string): string[] => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const images: string[] = [];
        doc.querySelectorAll('img').forEach(img => {
            if (img.src.startsWith('data:image')) {
                images.push(img.src);
            }
        });
        return images;
    };

    const dataURLtoFile = (dataurl: string, filename: string): File => {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const formData = new FormData();

            const newsData = {
                title: form.title,
                displayDescription: form.displayDescription,
                status: form.status,
                content: {
                    html: form.content
                }
            };

            formData.append('news', new Blob([JSON.stringify(newsData)], {
                type: 'application/json'
            }));

            const images = extractImagesFromHTML(form.content);
            for (const image of images) {
                if (image.startsWith('data:image')) {
                    const file = dataURLtoFile(image, `image_${Date.now()}.png`);
                    formData.append('images', file);
                }
            }

            if (isNew) {
                await api.post('/admin/news', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else if (id) {
                const newsId = parseInt(id, 10);
                if (isNaN(newsId)) {
                    throw new Error('Неверный ID новости');
                }
                await api.put(`/admin/news/${newsId}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            navigate('/admin/news');
        } catch (error) {
            console.error('Ошибка сохранения:', error);
            alert('Ошибка при сохранении новости');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className={s.loading}>Загрузка...</div>;
    }

    return (
        <div className={s.editPage}>
            <button onClick={() => navigate('/admin/news')} className={s.backBtn}>
                ← Назад
            </button>

            <div className={s.header}>
                <h1 className={s.title}>{isNew ? 'Создание новости' : 'Редактирование новости'}</h1>
                <button
                    className={s.saveBtn}
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Сохранение...' : (isNew ? 'Создать' : 'Сохранить')}
                </button>
            </div>

            <div className={s.editCard}>
                <div className={s.formGroup}>
                    <label className={s.label}>Заголовок</label>
                    <input
                        type="text"
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        className={s.input}
                        placeholder="Введите заголовок новости"
                    />
                </div>

                <div className={s.formGroup}>
                    <label className={s.label}>Подзаголовок новости</label>
                    <input
                        type="text"
                        name="displayDescription"
                        value={form.displayDescription}
                        onChange={handleChange}
                        className={s.input}
                        placeholder="Введите подзаголовок новости"
                    />
                </div>

                <div className={s.formGroup}>
                    <label className={s.label}>Статус</label>
                    <select
                        name="status"
                        value={form.status}
                        onChange={handleChange}
                        className={s.select}
                    >
                        <option value="draft">Черновик</option>
                        <option value="published">Опубликовано</option>
                        <option value="archived">Архив</option>
                    </select>
                </div>

                <div className={s.formGroup}>
                    <label className={s.label}>Содержание</label>
                    <WordEditor
                        content={form.content}
                        onChange={handleContentChange}
                        placeholder="Начните писать новость..."
                    />
                </div>
            </div>
        </div>
    );
};